import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Award, ExternalLink, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { publicAPI } from '../lib/api';

const WINNERS = [
  {
    place: 1,
    label: 'Winner',
    name: 'Ayokunle Durojaiye',
    project: 'Design and Development of a Biomimetic Dialysis High Flux Membrane Mechanism for Improved Hemodynamics Performance',
    supervisor: 'Prof. B.O. Malomo',
    prize: '₦500,000',
    lecturerPrize: '₦200,000',
    color: 'var(--gold)',
    image: '/images/season2/cheque_winner.jpg',
  },
  {
    place: 2,
    label: '1st Runner-Up',
    name: 'Samuel Olowosile',
    project: 'Theft Detection in Electrical Metering System',
    supervisor: 'Dr. Kehinde Ariyo',
    prize: '₦300,000',
    lecturerPrize: '₦200,000',
    color: '#c0c0c0',
    image: '/images/season2/cheque_runner_up_1.jpg',
  },
  {
    place: 3,
    label: '2nd Runner-Up',
    name: 'Sodiq Abdulrahman',
    project: 'Development of a Machine Learning Based Object Detection and Robotic Pick-and-Place Waste Sorting System',
    supervisor: 'Dr. A.A. Olawole',
    prize: '₦200,000',
    lecturerPrize: '₦200,000',
    color: '#cd7f32',
    image: '/images/season2/cheque_runner_up_2.jpg',
  },
];

const GALLERY = [
  '/images/season2/audience_01.jpg',
  '/images/season2/podium_dignitary.jpg',
  '/images/season2/dignitary_wall.jpg',
  '/images/season2/slide_perovskite.jpg',
  '/images/season2/slide_cnc.jpg',
  '/images/season2/voices_female.jpg',
  '/images/season2/voices_male.jpg',
];

export default function Season2Page() {
  const [judges, setJudges] = useState([]);
  const [topics, setTopics] = useState([]);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    publicAPI.getJudges().then(r => setJudges(r.data || [])).catch(() => {});
    publicAPI.getTopics({ season: 'Season II', limit: 100 }).then(r => {
      const all = r.data.topics || [];
      const d = new Date();
      let seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
      const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
      const shuffled = [...all].sort(() => rand() - 0.5);
      setTopics(shuffled.slice(0, 5));
    }).catch(() => {});
  }, []);

  return (
    <div style={{ background: 'var(--surface-body)', minHeight: '100vh', color: 'var(--text-primary)' }}>
      {/* Nav */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 40px', background: 'rgba(10,12,20,0.85)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          <ArrowLeft size={16} /> Back to Home
        </Link>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--gold)' }}>Futurum Innovation Awards</div>
        <Link to="/season1" style={{ fontSize: '0.875rem', color: 'var(--teal)' }}>Season I ↗</Link>
      </nav>

      {/* Hero */}
      <div style={{ paddingTop: 72, background: 'linear-gradient(135deg, rgba(201,162,39,0.08) 0%, rgba(45,212,191,0.04) 50%, transparent 100%)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '80px 40px 60px', textAlign: 'center' }}>
          <div className="eyebrow" style={{ marginBottom: 16 }}>Grand Finale &middot; 30 July 2026 &middot; African Centre of Excellence, OAU</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 300, lineHeight: 1.1, marginBottom: 24 }}>
            Season <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>Two</span> Highlights
          </h1>
          <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', maxWidth: 640, margin: '0 auto 40px', lineHeight: 1.7 }}>
            Six finalist teams took the stage before a panel of industry judges. Three emerged as champions, recognised alongside the lecturers who supervised them.
          </p>
          <img src="/images/season2/stage_wide.jpg" alt="Season II Grand Finale" style={{ width: '100%', borderRadius: 16, border: '1px solid rgba(201,162,39,0.2)', marginBottom: 48, objectFit: 'cover' }} />
          <div style={{ display: 'flex', justifyContent: 'center', gap: 48, flexWrap: 'wrap' }}>
            {[['435', 'Students Registered'], ['6', 'Finalist Teams'], ['₦16M', 'Total Value Awarded'], ['7', 'Industry Judges']].map(([val, label]) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--gold)', fontWeight: 600 }}>{val}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* YouTube Video */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '60px 40px 0' }}>
        <div className="eyebrow" style={{ textAlign: 'center', marginBottom: 16 }}>Event Recording</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 400, textAlign: 'center', marginBottom: 32 }}>Watch the Season II Finale</h2>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(201,162,39,0.2)' }}>
          <iframe
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            src="https://www.youtube.com/embed/PWNLWsBFvwc"
            title="Futurum Innovation Awards Season II Finale"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>

      {/* Winners */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 40px 0' }}>
        <div className="eyebrow" style={{ textAlign: 'center', marginBottom: 16 }}>Award Winners</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', fontWeight: 400, textAlign: 'center', marginBottom: 48 }}>
          The <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>Champions</span> of Season Two
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
          {WINNERS.map(w => (
            <div key={w.place} className="card" style={{ border: '1px solid ' + w.color + '33', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, ' + w.color + ', transparent)' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: w.color + '22', border: '1px solid ' + w.color + '44', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {w.place === 1 ? <Trophy size={18} style={{ color: w.color }} /> : <Award size={18} style={{ color: w.color }} />}
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', textTransform: 'uppercase', color: w.color }}>{w.label}</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500 }}>{w.name}</div>
                </div>
                <div style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', color: w.color, fontWeight: 600 }}>{w.prize}</div>
              </div>
              <img src={w.image} alt={w.name} style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 8, marginBottom: 16 }} />
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 12 }}>{w.project}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid var(--border-card)' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Supervisor: {w.supervisor}</div>
                <div style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--teal)' }}>Lecturer: {w.lecturerPrize}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gallery */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 40px 0' }}>
        <div className="eyebrow" style={{ textAlign: 'center', marginBottom: 16 }}>Event Gallery</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 400, textAlign: 'center', marginBottom: 40 }}>Moments from the Finale</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => setGalleryIndex(i => (i - 1 + GALLERY.length) % GALLERY.length)} aria-label="Previous photos"
            style={{ flexShrink: 0, width: 44, height: 44, borderRadius: '50%', border: '1px solid rgba(201,162,39,0.3)', background: 'rgba(201,162,39,0.06)', color: 'var(--gold)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronLeft size={20} />
          </button>
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[0, 1, 2].map(offset => {
              const src = GALLERY[(galleryIndex + offset) % GALLERY.length];
              return (
                <div key={offset} style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', aspectRatio: '3/2' }}>
                  <img src={src} alt="Season II finale moment" style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.3s ease' }}
                    onMouseEnter={e => e.target.style.transform = 'scale(1.03)'}
                    onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                  />
                </div>
              );
            })}
          </div>
          <button onClick={() => setGalleryIndex(i => (i + 1) % GALLERY.length)} aria-label="Next photos"
            style={{ flexShrink: 0, width: 44, height: 44, borderRadius: '50%', border: '1px solid rgba(201,162,39,0.3)', background: 'rgba(201,162,39,0.06)', color: 'var(--gold)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
      {/* Research Topics */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 40px 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 16 }}>Research Topics</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 400, marginBottom: 20, lineHeight: 1.2 }}>
              The projects behind <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>Season Two.</span>
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: 32 }}>
              These are the research topics final-year students tackled across Season II — proposed by OAU engineering lecturers and taken from the lecture theatre to the finale stage. Browse the full set.
            </p>
            <Link to="/topics?season=Season%20II" className="btn btn-primary">
              Browse All Season II Topics <ArrowRight size={16} />
            </Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {topics.map((topic, i) => (
              <Link to="/topics?season=Season%20II" key={topic.id} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 20, padding: '20px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', color: 'inherit', transition: 'all 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.paddingLeft='8px'}
                onMouseLeave={e => e.currentTarget.style.paddingLeft='0'}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--gold)', opacity: 0.6, minWidth: 24 }}>0{i+1}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: 500, lineHeight: 1.4, marginBottom: 4 }}>{topic.title}</div>
                  {topic.domain && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em' }}>{topic.domain}</div>}
                </div>
                <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 999, background: topic.is_sponsored ? 'rgba(42,157,143,0.1)' : 'rgba(201,162,39,0.08)', color: topic.is_sponsored ? 'var(--teal)' : 'var(--gold)', border: '1px solid ' + (topic.is_sponsored ? 'rgba(42,157,143,0.2)' : 'rgba(201,162,39,0.15)'), fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                  {topic.is_sponsored ? 'Sponsored' : 'Open'}
                </span>
              </Link>
            ))}
            <Link to="/topics?season=Season%20II" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 20, fontSize: '0.85rem', color: 'var(--teal)', textDecoration: 'none', fontWeight: 500 }}>
              See all Season II topics <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
      {/* Judges */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 40px 0' }}>
        <div className="eyebrow" style={{ textAlign: 'center', marginBottom: 16 }}>Panel of Judges</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 400, textAlign: 'center', marginBottom: 40 }}>
          Industry <span style={{ color: 'var(--teal)', fontStyle: 'italic' }}>Experts</span>
        </h2>
        {judges.length === 0 ? null : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 20 }}>
            {judges.map(j => (
              <div key={j.id} className="card" style={{ textAlign: 'center', padding: 24 }}>
                {j.headshot_url ? (
                  <img src={j.headshot_url} alt={j.first_name + ' ' + j.last_name} style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 12px', border: '2px solid rgba(201,162,39,0.3)' }} />
                ) : (
                  <div style={{ width: 80, height: 80, borderRadius: '50%', margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(201,162,39,0.1)', border: '2px solid rgba(201,162,39,0.3)', fontFamily: 'var(--font-display)', color: 'var(--gold)', fontSize: '1.4rem' }}>
                    {j.first_name && j.first_name[0]}{j.last_name && j.last_name[0]}
                  </div>
                )}
                <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: 4 }}>{j.first_name} {j.last_name}</div>
                {j.affiliation && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 12 }}>{j.affiliation}</div>}
                {j.linkedin_url && (
                  <a href={j.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--teal)' }}>
                    LinkedIn <ExternalLink size={10} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scoring Criteria */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '80px 40px 0' }}>
        <div className="eyebrow" style={{ textAlign: 'center', marginBottom: 16 }}>Judging Framework</div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 400, textAlign: 'center', marginBottom: 40 }}>Scoring Criteria</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16 }}>
          {[
            { title: 'Originality and Creativity', points: 20, desc: 'Innovation, out-of-the-box thinking, and creative problem solving.' },
            { title: 'Technical Merit', points: 20, desc: 'Technical proficiency, execution quality, and clarity of explanation.' },
            { title: 'Feasibility', points: 20, desc: 'Real-world viability given current resources and technology.' },
            { title: 'Impact', points: 20, desc: 'Positive impact on society, environment, or the target industry.' },
            { title: 'Presentation and Delivery', points: 20, desc: 'Clarity, engagement, and quality of presentation materials.' },
          ].map(c => (
            <div key={c.title} className="card card-teal" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{c.title}</div>
                <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)', fontSize: '0.85rem' }}>{c.points} pts</div>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{c.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '48px 40px 24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 80 }}>
        Futurum Innovation Awards &middot; A Futurum Innovation Ltd initiative
      </div>
    </div>
  );
}
